import { RootModelSearch } from '@shared/datastore/root.model';
import { BackendFetchTypes } from '../backend';
import { transformCurrencyAjax } from '../currency/currency.transformers';
import { assertNever, toNumber } from '../helpers';
import {
  SearchContestEntryAjax,
  SearchContestResult,
  SearchFreelancersEntryAjax,
  SearchFreelancersResult,
  SearchKeywordProjectsEntryAjax,
  SearchKeywordProjectsResult,
} from './search.model';

function handleProfileImageUnknowns(url: string) {
  return url.includes('img/unknown.png') ? undefined : url;
}

function getFlagCodeFromFlagIcon(url: string) {
  return (url.match(/img\/flags\/png\/(\w+)\.png/) || [])[1] || '';
}

function transformOneZero(value: '0' | '1' | 0 | 1 | null): boolean {
  return value === 1 || value === '1';
}

export function transformSearchContest(
  item: SearchContestEntryAjax,
): SearchContestResult {
  return {
    bidAvgUsd: item.bid_avg_usd,
    bidCount: item.bid_count,
    bidEndDate: item.bid_enddate,
    contestType: item.contest_type,
    country: item.country,
    currency: transformCurrencyAjax(item.currency),
    entryType: item.entry_type,
    extended: transformOneZero(item.extended),
    featured: transformOneZero(item.featured),
    hideBids: transformOneZero(item.hidebids),
    highlight: transformOneZero(item.highlight),
    id: item.id,
    language: item.language,
    locked: transformOneZero(item.locked),
    maxBudget: item.maxbudget,
    maxBudgetUsd: item.maxbudget_usd,
    minBudget: item.minbudget,
    minBudgetUsd: item.minbudget_usd,
    name: item.name,
    nda: item.NDA === 'Y',
    neglected: transformOneZero(item.neglected),
    nonpublic: transformOneZero(item.nonpublic),
    prize: item.prize,
    projectDesc: item.project_desc,
    projectId: item.project_id,
    projectName: item.project_name,
    projectType: item.project_type,
    skill: item.skill,
    skills: item.skills,
    state: toNumber(item.state),
    status: toNumber(item.status),
    submitDate: item.submitdate,
    usersId: toNumber(item.users_id),
  };
}

export function transformSearchKeywordProjects(
  item: SearchKeywordProjectsEntryAjax,
): SearchKeywordProjectsResult {
  return {
    id: toNumber(item.id),
    currency: transformCurrencyAjax(item.currency),
    jobList: item.jobList,
    maxBudget: toNumber(item.max_budget),
    minBudget: toNumber(item.min_budget),
    title: item.name,
    type: item.budget_period === 'Hourly:' ? 'hourly' : 'fixed',
  };
}

export function transformSearchFreelancers(
  item: SearchFreelancersEntryAjax,
): SearchFreelancersResult {
  return {
    completion: item.completion,
    flagIcon: item.flag_icon,
    flagCode: getFlagCodeFromFlagIcon(item.flag_icon),
    flagName: item.flag_name,
    id: item.id,
    jobList: item.jobList,
    profileLogoUrl: handleProfileImageUnknowns(item.profile_logo_url),
    publicName: item.public_name,
    reputation: item.reputation,
    reviews: item.reviews,
    username: item.username,
  };
}

export function transformSearch<K extends keyof RootModelSearch>(
  key: K,
  result: BackendFetchTypes[K],
): RootModelSearch[K] {
  const key2: keyof RootModelSearch = key; // https://github.com/Microsoft/TypeScript/issues/20840

  switch (key2) {
    case 'searchContests': {
      return (result as BackendFetchTypes['searchContests']).map(
        transformSearchContest,
      );
    }
    case 'searchKeywordProjects':
      return (result as BackendFetchTypes['searchKeywordProjects']).map(
        transformSearchKeywordProjects,
      );
    case 'searchFreelancers':
      return (result as BackendFetchTypes['searchFreelancers']).map(
        transformSearchFreelancers,
      );
    default:
      return assertNever(key2);
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/search/search.transformers.ts